import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import {
  grantedRoleLabel,
  useAnswerInvitation,
  useCancelRequest,
  useMyInvitations,
  useMyRequests,
} from '@/entities/collab';
import { FormError } from '@/shared/forms';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import styles from './InvitationsPage.module.css';

/** Pending invitations to answer and the viewer's own pending join requests. */
export function InvitationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invitations = useMyInvitations();
  const requests = useMyRequests();
  const answer = useAnswerInvitation();
  const cancel = useCancelRequest();

  if (invitations.isPending || requests.isPending) return <LoadingState />;
  if (invitations.isError) {
    return <ErrorState error={invitations.error} onRetry={() => void invitations.refetch()} />;
  }
  const none = invitations.data.length === 0 && (requests.data?.length ?? 0) === 0;
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1>{t('invitations.title')}</h1>
        <Link to="/app">{t('common.backHome')}</Link>
      </header>
      <FormError error={answer.error ?? cancel.error} />
      {none && <EmptyState title={t('invitations.empty')} />}
      {invitations.data.length > 0 && (
        <section aria-labelledby="inv-title">
          <h2 id="inv-title">{t('invitations.pending')}</h2>
          <ul className={styles.list}>
            {invitations.data.map((invitation) => (
              <li key={invitation.id} className={styles.item} data-testid="invitation">
                <div className={styles.text}>
                  <strong>
                    {t(
                      invitation.scope === 'workspace'
                        ? 'invitations.toWorkspace'
                        : invitation.scope === 'database'
                          ? 'invitations.toDatabase'
                          : 'invitations.toBoard',
                      { name: invitation.target_name },
                    )}
                  </strong>
                  <span className={styles.meta}>
                    {t('invitations.from', { who: invitation.inviter_display_name ?? '—' })} ·{' '}
                    {grantedRoleLabel(t, invitation.role, invitation.custom_role_name)} ·{' '}
                    {t('collab.invite.expires', {
                      when: formatDateTime(invitation.expires_at, i18n.language),
                    })}
                  </span>
                  {invitation.scope === 'database' && (
                    <span className={styles.meta}>
                      {invitation.current_access?.role
                        ? t('invitations.databaseAlready', {
                            role: t(`roles.${invitation.current_access.role}`),
                          })
                        : t('invitations.databaseNew')}
                    </span>
                  )}
                </div>
                <div className={styles.actions}>
                  <Button
                    variant="primary"
                    disabled={answer.isPending}
                    onClick={() => {
                      answer.mutate(
                        { invitationId: invitation.id, accept: true },
                        {
                          onSuccess: (result) => {
                            if (result?.database_id) {
                              void navigate(`/app/databases/${result.database_id}`);
                            } else if (result?.board_id) {
                              void navigate(`/app/boards/${result.board_id}`);
                            } else if (result?.workspace_id) {
                              void navigate(`/app/workspaces/${result.workspace_id}`);
                            }
                          },
                        },
                      );
                    }}
                  >
                    {t('invitations.accept')}
                  </Button>
                  <Button
                    disabled={answer.isPending}
                    onClick={() => {
                      answer.mutate({ invitationId: invitation.id, accept: false });
                    }}
                  >
                    {t('invitations.decline')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(requests.data?.length ?? 0) > 0 && (
        <section aria-labelledby="req-title">
          <h2 id="req-title">{t('invitations.myRequests')}</h2>
          <ul className={styles.list}>
            {(requests.data ?? []).map((request) => (
              <li key={request.id} className={styles.item}>
                <div className={styles.text}>
                  <strong>
                    {t(
                      request.kind === 'join_board'
                        ? 'invitations.requestBoard'
                        : request.kind === 'board_transfer'
                          ? 'invitations.requestTransfer'
                          : 'invitations.requestWorkspace',
                    )}
                  </strong>
                  <span className={styles.meta}>
                    {t('invitations.waitingApproval')} ·{' '}
                    {formatDateTime(request.created_at, i18n.language)}
                  </span>
                </div>
                <Button
                  disabled={cancel.isPending}
                  onClick={() => {
                    cancel.mutate(request.id);
                  }}
                >
                  {t('invitations.cancelRequest')}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
